import { ElementType } from '@ritabygglov/core';
import { useCallback, useState } from 'react';
import { UpdateCommand } from '../../../commands/UpdateCommand';
import { CommandManager } from '../../../commands/CommandManager';
import CanvasSession from '../../../services/CanvasSession';
import {
  AppState,
  ClientCoordinates,
  SelectionMode,
  SelectionModes,
  ViewBox,
} from '../../../types';
import { elementHandlerRegistry } from '../handlers/elementHandlers';
import { ApexDragContext } from '../types/interactionTypes';

interface ApexDragInfo {
  elementId: string;
}

export interface ApexDraggingDeps {
  appState: AppState;
  session: CanvasSession;
  commandManager: CommandManager;
  selectionMode: SelectionMode;
  setSelectionMode: React.Dispatch<React.SetStateAction<SelectionMode>>;
  setSelectedElements: React.Dispatch<React.SetStateAction<string[]>>;
  viewBox: ViewBox;
}

export const useApexDragging = (hooks: ApexDraggingDeps) => {
  const {
    appState,
    session,
    commandManager,
    setSelectionMode,
    selectionMode,
    setSelectedElements,
    viewBox,
  } = hooks;

  const [apexDragInfo, setApexDragInfo] = useState<ApexDragInfo | null>(null);

  const onStartApexDrag = useCallback(
    (_clientCoordinates: ClientCoordinates, id: string) => {
      const elementId = id.split('-apex')[0];
      const element = appState.elements[elementId];
      if (
        !element ||
        (element.type !== ElementType.Triangle &&
          element.type !== ElementType.Angle)
      ) {
        return;
      }

      setApexDragInfo({ elementId });
      setSelectedElements([elementId]);
      setSelectionMode({
        ...selectionMode,
        type: SelectionModes.ApexDragging,
      });
    },
    [appState.elements, setSelectionMode, selectionMode, setSelectedElements]
  );

  const onApexDragging = useCallback(
    (clientCoordinates: ClientCoordinates, isEphemeral = true) => {
      if (!apexDragInfo) return;

      const element = appState.elements[apexDragInfo.elementId];
      if (!element) return;

      const context: ApexDragContext = {
        element,
        coordinates: clientCoordinates,
        viewBox,
      };

      const updatedElement = elementHandlerRegistry.handleApexDrag(
        element.type,
        context
      );
      if (!updatedElement) return;

      const command = new UpdateCommand(
        [updatedElement],
        [element],
        session.currentUserId,
        isEphemeral
      );
      commandManager.executeCommand(command);
    },
    [
      apexDragInfo,
      appState.elements,
      session.currentUserId,
      commandManager,
      viewBox,
    ]
  );

  const onStopApexDrag = useCallback(() => {
    if (apexDragInfo) {
      setApexDragInfo(null);
      setSelectionMode({
        ...selectionMode,
        type: SelectionModes.None,
      });
    }
  }, [apexDragInfo, setSelectionMode, selectionMode]);

  return {
    onStartApexDrag,
    onApexDragging,
    onStopApexDrag,
  };
};
